import { useState, useEffect, useRef } from "react";
import { FiSend, FiTrash2, FiArrowLeft, FiRefreshCw } from "react-icons/fi";
import {
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Paper,
  TextField,
  Button,
  Card,
  CardContent,
  Divider,
  Menu,
  MenuItem,
} from "@mui/material";

const API_URL = process.env.REACT_APP_BASE_URL;

const GRADIENT = "linear-gradient(110deg, rgba(75,108,183,1) 11.3%, rgba(24,40,72,1) 99.3%)";

export default function ChatApp() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [recipientId, setRecipientId] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [chatStarted, setChatStarted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [apiResponse, setApiResponse] = useState(null);
  const messagesEndRef = useRef(null);

  // Context menu state
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const employeeId = sessionStorage.getItem("employeeId") || "";
  const classId = sessionStorage.getItem("classId") || "";
  const sectionId = sessionStorage.getItem("sectionId") || "";
  const authToken = sessionStorage.getItem("token") || "YOUR_AUTH_TOKEN";

  // Effect 1: Initial fetch on component mount
useEffect(() => {
  fetchMessages();
}, []);

// Effect 2: Poll messages every 5 seconds
useEffect(() => {
  const interval = setInterval(() => {
    fetchMessages();
  }, 5000);
  return () => clearInterval(interval);
}, []);

// Effect 3: Mark messages as read every 5 seconds
useEffect(() => {
  markMessagesAsRead();
  const readInterval = setInterval(() => {
    markMessagesAsRead();
  }, 5000);
  return () => clearInterval(readInterval);
}, []);

// Effect 4: Auto-scroll to bottom when messages change
useEffect(() => {
  if (messagesEndRef.current) {
    messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
  }
}, [messages]);

  const parseCustomDate = (dateStr) => {
    if (!dateStr) return new Date();
    const regex = /^([A-Za-z]+) (\d{1,2}) (\d{4})\s+(\d{1,2}):(\d{2}):(\d{2}):(\d{3})(AM|PM)?$/;
    const match = dateStr.match(regex);
    if (!match) return new Date();
    const [, month, day, year, hour, minute, second, millisecond, period] = match;
    const monthMap = {
      Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
      Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
    };
    let hour24 = parseInt(hour, 10);
    if (period === "PM" && hour24 !== 12) hour24 += 12;
    if (period === "AM" && hour24 === 12) hour24 = 0;
    return new Date(
      parseInt(year, 10),
      monthMap[month],
      parseInt(day, 10),
      hour24,
      parseInt(minute, 10),
      parseInt(second, 10),
      parseInt(millisecond, 10)
    );
  };

  const formatCustomDate = (date) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    const milliseconds = date.getMilliseconds().toString().padStart(3, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const hour12 = hours % 12 || 12;
    return `${month} ${day} ${year} ${hour12}:${minutes}:${seconds}:${milliseconds}${ampm}`;
  };

  const formatDate = (dateStr) => {
    const date = parseCustomDate(dateStr);
    if (isNaN(date.getTime())) return "Invalid Date";
    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Check if message can be deleted (within 30 minutes)
  const canDeleteMessage = (message) => {
    if (!message.isSent && message.direction !== "Sent") return false;
    
    const timestamp = message.timestamp || message.sentDate;
    if (!timestamp) return false;

    const messageDate = parseCustomDate(timestamp);
    const now = new Date();
    const diffInMinutes = (now - messageDate) / (1000 * 60);
    
    return diffInMinutes <= 30;
  };

  const processMessages = (allMessages) => {
    const processedMessages = allMessages.map(msg => {
      const timestamp = msg.direction === "Sent" ? msg.sentDate : msg.receivedDate;
      return {
        ...msg,
        isSent: msg.direction === "Sent" || msg.isSent === true,
        timestamp: timestamp
      };
    });
    return [...processedMessages].sort((a, b) => {
      const timeA = parseCustomDate(a.timestamp || a.sentDate || a.receivedDate);
      const timeB = parseCustomDate(b.timestamp || b.sentDate || b.receivedDate);
      return timeA - timeB;
    });
  };

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const payload = {  empId: sessionStorage.getItem('teacherId') , classId, sectionId };
      if (recipientId) payload.recipientId = recipientId;
      const response = await fetch(`${API_URL}/Chats/GetMessages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: authToken,
        },
        body: JSON.stringify(payload),
      });
      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
        const recipientName = data.receivedMessages?.[0]?.recipientName;
        if (recipientName) setRecipientName(recipientName);
        const recipientID = data.sentMessages?.[0]?.recipientId;
        if (recipientID) setRecipientId(recipientID);
      } catch (e) {
        console.error("Failed to parse response as JSON:", e);
        setError("Invalid response format from server");
        return;
      }
      if (!response.ok) throw new Error(`Server error: ${response.status} - ${responseText}`);
      const allMessages = [
        ...(data.sentMessages || []).map(msg => ({
          ...msg,
          direction: "Sent",
          isSent: true,
          timestamp: msg.sentDate
        })),
        ...(data.receivedMessages || []).map(msg => ({
          ...msg,
          direction: "Received",
          isSent: false,
          timestamp: msg.receivedDate
        }))
      ];
      const processedMessages = processMessages(allMessages);
      const finalMessages = processedMessages.filter(msg => {
        if (msg.temp) {
          const exists = processedMessages.some(
            apiMsg => !apiMsg.temp && apiMsg.messageText === msg.messageText && apiMsg.direction === "Sent"
          );
          return !exists;
        }
        return true;
      });
      setApiResponse(data);
      setMessages(finalMessages);
      markMessagesAsRead();
    } catch (error) {
      console.error("Error fetching messages:", error);
      setError(`Failed to load messages: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const markMessagesAsRead = async () => {
    try {
      const payload = { empId: employeeId };
      const response = await fetch(`${API_URL}/Chats/ReadMessage`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: authToken,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`Failed to mark messages as read: ${response.status}`);
    } catch (error) {
      console.error("Error marking messages as read:", error);
    }
  };

  const sendMessage = async () => {
    const recipientIdString = recipientId ? String(recipientId).trim() : "";
    const now = new Date();
    const formattedDate = formatCustomDate(now);
    const tempId = `temp-${Date.now()}`;
    const newMessage = {
      messageId: tempId,
      isSent: true,
      direction: "Sent",
      messageText: input,
      sentDate: formattedDate,
      timestamp: formattedDate,
      senderName: "You",
      temp: true,
    };
    setMessages(prevMessages => processMessages([...prevMessages, newMessage]));
    const currentInput = input;
    setInput("");
    try {
      const data = {
        senderId: employeeId,
        recipientId: 0,
        classId,
        sectionId,
        messageText: currentInput,
      };
      const response = await fetch(`${API_URL}/Chats/SendMessageFromParents`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: authToken,
        },
        body: JSON.stringify(data),
      });
      const responseText = await response.text();
      if (!response.ok) throw new Error(`Failed to send message: ${response.status}`);
      setMessages(prevMessages =>
        prevMessages.map(msg =>
          msg.messageId === tempId ? { ...msg, temp: false, sent: true } : msg
        )
      );
      setTimeout(fetchMessages, 1000);
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages(prevMessages =>
        prevMessages.map(msg =>
          msg.messageId === tempId ? { ...msg, failed: true } : msg
        )
      );
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const exitChat = () => {
    setChatStarted(false);
    setMessages([]);
    setApiResponse(null);
  };

  const manualRefresh = () => {
    fetchMessages();
  };

  // Handle right-click on message
  const handleContextMenu = (event, message, index) => {
    event.preventDefault();
    
    // Only show context menu for sent messages
    if (!message.isSent && message.direction !== "Sent") return;
    
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4 }
        : null
    );
    setSelectedMessage({ message, index });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
    setSelectedMessage(null);
  };

  const deleteMessage = async () => {
    if (!selectedMessage) return;

    const { message, index } = selectedMessage;

    // Check if message can be deleted
    if (!canDeleteMessage(message)) {
      alert("You can only delete messages within 30 minutes of sending.");
      handleCloseContextMenu();
      return;
    }

    setMessages(prevMessages => prevMessages.filter((_, i) => i !== index));
    handleCloseContextMenu();

    if (message.temp) return;

    try {
      const response = await fetch(`${API_URL}/Chats/${message.messageId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: authToken,
        },
      });
      if (!response.ok) throw new Error("Failed to delete message");
      fetchMessages();
    } catch (error) {
      console.error("Error deleting message:", error);
    }
  };

  return (
    <Box sx={{ px: 3, py: 0 }}>
      <Card sx={{ maxWidth: "100%", boxShadow: 4, borderRadius: 2 }}>
        <CardContent sx={{ p: 0 }}>
          {/* Header */}
          <Box
            sx={{
              background: GRADIENT,
              color: "#fff",
              p: 2.5,
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Chat with {recipientName || "Teacher"}
            </Typography>
            <IconButton sx={{ color: "#fff" }} onClick={manualRefresh}>
              <FiRefreshCw className={loading ? "animate-spin" : ""} />
            </IconButton>
          </Box>

          <Divider />

          {/* Messages */}
          <Box
            sx={{
              height: "420px",
              overflowY: "auto",
              p: 2,
              backgroundColor: "#f5f7fb",
            }}
          >
            {loading && messages.length === 0 ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Typography variant="body2" color="error" sx={{ textAlign: "center", mb: 2 }}>
                {error}
              </Typography>
            ) : messages.length > 0 ? (
              messages.map((msg, index) => {
                const isSent = msg.direction === "Sent" || msg.isSent === true;
                const messageText = msg.messageText || "No text";
                const senderName = isSent ? "You" : msg.senderName || "Teacher";
                const timestamp = msg.timestamp || msg.sentDate || msg.receivedDate;
                const formattedTime = timestamp ? formatDate(timestamp) : "Time unknown";
                return (
                  <Box
                    key={`${msg.messageId}-${index}`}
                    sx={{
                      display: "flex",
                      justifyContent: isSent ? "flex-end" : "flex-start",
                      mb: 1.5,
                    }}
                  >
                    <Paper
                      onContextMenu={(e) => handleContextMenu(e, msg, index)}
                      sx={{
                        p: 1.5,
                        maxWidth: "70%",
                        backgroundColor: isSent ? "#4b6cb7" : "#ffffff",
                        color: isSent ? "#fff" : "#000",
                        borderRadius: 2,
                        cursor: isSent ? "context-menu" : "default",
                        userSelect: "none",
                      }}
                    >
                      <Typography variant="body2">{messageText}</Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          textAlign: "right",
                          opacity: 0.7,
                          mt: 0.5,
                        }}
                      >
                        {formattedTime}
                      </Typography>
                    </Paper>
                  </Box>
                );
              })
            ) : (
              <Typography variant="body2" color="textSecondary" sx={{ textAlign: "center", py: 6 }}>
                No messages yet. Start the conversation!
              </Typography>
            )}
            <div ref={messagesEndRef} />
          </Box>

          <Divider />

          {/* Input */}
          <Box sx={{ p: 2, display: "flex", gap: 1 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Type a message..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              sx={{ backgroundColor: "#fff", borderRadius: 1 }}
            />
            <Button
              variant="contained"
              onClick={sendMessage}
              disabled={!input.trim()}
              sx={{
                background: GRADIENT,
                '& svg': {
                  color: '#fff',
                },
              }}
            >
              <FiSend />
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem
          onClick={deleteMessage}
          disabled={selectedMessage && !canDeleteMessage(selectedMessage.message)}
          sx={{
            color: selectedMessage && !canDeleteMessage(selectedMessage.message) ? 'gray' : 'red',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <FiTrash2 size={16} />
          {selectedMessage && !canDeleteMessage(selectedMessage.message)
            ? "Delete message"
            : "Delete message"}
        </MenuItem>
      </Menu>
    </Box>
  );
}